import React from "react";
import { createSwitch, SwitchFrame, type SwitchProps, SwitchStyledContext, SwitchThumb, styled, withStaticProperties } from "tamagui";
import { Check as CheckIcon } from "@tamagui/lucide-icons";
import { borderSizeTokens } from "../../themes/token-space";
import { Icon } from "../Icon";
import { animateOnly } from "../Motion/motion";
const Frame = styled(SwitchFrame, {
  overflow: "hidden",
  padding: "$sp0.25",
  cursor: "pointer",
  variants: {
    unstyled: {
      false: {
        backgroundColor: "$surface0Hover",
        focusVisibleStyle: {
          outlineWidth: borderSizeTokens.border3,
          outlineColor: "$borderIntense",
          outlineStyle: "solid",
          outlineOffset: 1
        }
      }
    },
    size: {
      "...size": () => ({
        width: "$si6",
        height: "$si4",
        minHeight: "$si4"
      })
    },
    disabled: {
      true: {
        backgroundColor: "$bgDisabled",
        opacity: 1,
        cursor: "not-allowed"
      }
    }
  } as const
});
const Thumb = styled(SwitchThumb, {
  alignItems: "center",
  justifyContent: "center",
  variants: {
    unstyled: {
      false: {
        backgroundColor: "$surface2"
      }
    },
    size: {
      "...size": () => ({
        width: 28,
        // $si3.5
        height: 28 // $si3.5
      })
    }
  } as const
});
const InternalSwitch = createSwitch({
  Frame,
  Thumb
});
const ThumbIcon: React.FC = () => {
  const {
    active,
    disabled
  } = SwitchStyledContext.useStyledContext();
  if (!active) {
    return null;
  }
  return <Icon icon={CheckIcon} size="$iconSmall" color={disabled ? "$iconMuted" : "$iconDefault"} strokeWidth={2.5} data-sentry-element="Icon" data-sentry-component="ThumbIcon" data-sentry-source-file="Switch.tsx" />;
};
ThumbIcon.displayName = "SwitchThumbIcon";

/**
 * Bakes in the bouncy thumb-slide transition so consumers don't have to.
 * `width` is forwarded as a runtime prop so createSwitch can compute the
 * correct initial thumb offset without waiting for a layout pass.
 */
const AnimatedThumb = React.forwardRef<React.ComponentRef<typeof InternalSwitch.Thumb>, React.ComponentPropsWithRef<typeof InternalSwitch.Thumb>>((props, ref) => <InternalSwitch.Thumb transition="press" width="$si4" {...props} ref={ref} />);
AnimatedThumb.displayName = "SwitchThumb";
const SwitchImpl = React.forwardRef<React.ComponentRef<typeof InternalSwitch>, React.ComponentPropsWithRef<typeof InternalSwitch>>((props, ref) => {
  const innerRef = React.useRef<React.ComponentRef<typeof InternalSwitch>>(null);
  const setRef = React.useCallback((node: React.ComponentRef<typeof InternalSwitch> | null) => {
    innerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
      return;
    }
    if (ref != null) {
      ref.current = node;
    }
  }, [ref]);

  // Tamagui's `useSwitch` hook returns `switchProps` with `tabIndex: 0` (on web)
  // on every render, and `createSwitch` spreads it after user props — clobbering
  // any consumer override. We therefore patch the rendered element after every
  // render, not just on mount. Adding a dependency array would silently break
  // this: Tamagui re-applies `tabIndex: 0` on unrelated re-renders.
  React.useEffect(() => {
    const element = innerRef.current;
    if (element == null || typeof HTMLElement === "undefined" || !(element instanceof HTMLElement)) {
      return;
    }
    if (props.focusable === false || props.tabIndex === -1) {
      element.tabIndex = -1;
      return;
    }
    if (typeof props.tabIndex === "number") {
      element.tabIndex = props.tabIndex;
    }
  });
  return <InternalSwitch transition="fade" animateOnly={animateOnly("backgroundColor")} activeStyle={{
    backgroundColor: props.disabled ? "$bgDisabled" : "$surfaceStrong"
  }} {...props} ref={setRef} />;
});
SwitchImpl.displayName = "Switch";
export const Switch = withStaticProperties(SwitchImpl, {
  Thumb: AnimatedThumb,
  ThumbIcon
});
export type { SwitchProps };